import { type ComponentPropsWithoutRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { formatMoney } from '@repo/design-system-kit/utils/format-money';
import { Select } from '@repo/design-system-kit/components/select/select';

export const ANNUAL_TURNOVER_OPTIONS = [
  '0-50000',
  '50000-100000',
  '100000-200000',
  '200000-500000',
  '500000-1000000',
  '1000000-2000000',
  '2000000-5000000',
  '5000000-10000000',
  '10000000-50000000',
  '50000000-',
] as const;

export type AnnualTurnoverRange = (typeof ANNUAL_TURNOVER_OPTIONS)[number];

export interface AnnualTurnoverOption {
  code: AnnualTurnoverRange;
  name: string;
}

export interface AnnualTurnoverSelectProps extends Omit<
  ComponentPropsWithoutRef<'div'>,
  'onChange'
> {
  errorMessage?: string;
  isAssociation?: boolean;
  isExpectedTurnover?: boolean;
  isReadOnly?: boolean;
  onChange?: (value: AnnualTurnoverRange) => void;
  value?: AnnualTurnoverRange | null;
}

export function AnnualTurnoverSelect({
  className,
  errorMessage,
  isAssociation = false,
  isExpectedTurnover = false,
  isReadOnly,
  onChange,
  value,
  ...props
}: AnnualTurnoverSelectProps) {
  const intl = useIntl();

  const label = isExpectedTurnover
    ? intl.formatMessage({
        id: 'company-creation-kit.annual-turnover-select.expected-label',
      })
    : intl.formatMessage(
        {
          id: 'company-creation-kit.annual-turnover-select.label',
        },
        { isAssociation }
      );

  const description = intl.formatMessage(
    {
      id: isExpectedTurnover
        ? 'company-creation-kit.annual-turnover-select.expected-description'
        : 'company-creation-kit.annual-turnover-select.description',
    },
    { isAssociation }
  );

  const placeholder = intl.formatMessage(
    {
      id: 'company-creation-kit.annual-turnover-select.placeholder',
    },
    {
      amount: formatMoney({
        amount: 50000,
        locale: intl.locale,
        token: 'money-symbol-compact',
      }),
    }
  );

  const options: AnnualTurnoverOption[] = useMemo(() => {
    return ANNUAL_TURNOVER_OPTIONS.map(code => {
      const [minStr, maxStr] = code.split('-');
      const min = Number(minStr);
      const max = Number(maxStr);

      if (min === 0) {
        return {
          code,
          name: intl.formatMessage(
            { id: 'company-creation-kit.annual-turnover-select.option-less-than' },
            {
              amount: formatMoney({
                amount: max,
                locale: intl.locale,
                token: 'money-symbol-compact',
              }),
            }
          ),
        };
      }

      if (maxStr === '') {
        return {
          code,
          name: intl.formatMessage(
            { id: 'company-creation-kit.annual-turnover-select.option-more-than' },
            {
              amount: formatMoney({
                amount: min,
                locale: intl.locale,
                token: 'money-symbol-compact',
              }),
            }
          ),
        };
      }

      const name = intl.formatMessage(
        { id: 'company-creation-kit.annual-turnover-select.option-range' },
        {
          minAmount: formatMoney({
            amount: min,
            locale: intl.locale,
            token: 'money-symbol-compact',
          }),
          maxAmount: formatMoney({
            amount: max,
            locale: intl.locale,
            token: 'money-symbol-compact',
          }),
        }
      );
      return { code, name };
    });
  }, [intl]);

  return (
    <div className={className} data-test-annual-turnover {...props}>
      <Select
        data-test-annual-turnover-input
        description={errorMessage ? undefined : description}
        errorMessage={errorMessage}
        isInvalid={Boolean(errorMessage)}
        isReadOnly={isReadOnly}
        label={label}
        onSelectionChange={key => {
          if (typeof key === 'string') {
            onChange?.(key as AnnualTurnoverRange);
          }
        }}
        placeholder={placeholder}
        selectedKey={value}
      >
        {options.map(option => (
          <Select.Option
            data-test-annual-turnover-option={option.code}
            id={option.code}
            key={option.code}
          >
            {option.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
}
